import { useEffect, useRef, type ReactNode } from "react";
import {
  captureLearningEvent,
  type LearningEventName,
  type LearningEventProperties,
  type LearningTrackEvent,
} from "@/lib/analytics/learning-events";

interface LearningEventImpressionProps {
  event: LearningEventName;
  eventKey: string;
  properties: LearningEventProperties;
  trackEvent: LearningTrackEvent;
  enabled?: boolean;
  className?: string;
  children: ReactNode;
}

const IMPRESSION_THRESHOLD = 0.25;

/** Capture one impression only after the actual card enters the viewport. */
export function LearningEventImpression({
  event,
  eventKey,
  properties,
  trackEvent,
  enabled = true,
  className,
  children,
}: LearningEventImpressionProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const capturedKeyRef = useRef<string | null>(null);
  const latestPropertiesRef = useRef(properties);
  const latestTrackEventRef = useRef(trackEvent);

  latestPropertiesRef.current = properties;
  latestTrackEventRef.current = trackEvent;

  useEffect(() => {
    if (!enabled || capturedKeyRef.current === eventKey) return;
    const element = elementRef.current;
    if (!element || typeof window.IntersectionObserver !== "function") return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (
          !entries.some(
            (entry) =>
              entry.isIntersecting &&
              entry.intersectionRatio >= IMPRESSION_THRESHOLD,
          )
        )
          return;
        if (capturedKeyRef.current === eventKey) return;

        capturedKeyRef.current = eventKey;
        captureLearningEvent(
          latestTrackEventRef.current,
          event,
          latestPropertiesRef.current,
        );
        observer.disconnect();
      },
      { threshold: IMPRESSION_THRESHOLD },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, event, eventKey]);

  return (
    <div ref={elementRef} className={className}>
      {children}
    </div>
  );
}
